import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { auth as firebaseAuth } from "./firebase";

const API_URL = import.meta.env.VITE_API_URL || "/api";

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Handler invoked when an API call returns 401 (or 403 USER_NOT_PROVISIONED /
 * USER_DEACTIVATED). The default is a no-op so unit tests and pre-mount
 * callers don't blow up. `AuthProvider` calls `setUnauthorizedHandler` on
 * mount with a router-aware navigation.
 */
let unauthorizedHandler: () => void = () => {};

export function setUnauthorizedHandler(fn: () => void): void {
  unauthorizedHandler = fn;
}

/**
 * Handler invoked when an API call returns 403 PASSWORD_CHANGE_REQUIRED.
 * Set by `AuthProvider` on mount.
 */
let passwordChangeRequiredHandler: () => void = () => {};

export function setPasswordChangeRequiredHandler(fn: () => void): void {
  passwordChangeRequiredHandler = fn;
}

// Request interceptor: always send a fresh Firebase ID token. We await
// `authStateReady()` first so the first request after a hard refresh never
// races against Firebase SDK initialization.
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    await firebaseAuth.authStateReady();
    const fbUser = firebaseAuth.currentUser;
    if (fbUser) {
      try {
        const token = await fbUser.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch {
        // Token fetch failed; let the request go without auth so the response
        // interceptor can surface the 401.
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor to handle auth errors. Uses router-aware handlers
// rather than `window.location.href` so React state + unsaved input survive.
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string; errorCode?: string }>) => {
    if (error.response?.status === 401) {
      unauthorizedHandler();
    }

    if (error.response?.status === 403) {
      const errorCode =
        error.response.data?.errorCode || error.response.data?.error;

      if (errorCode === "PASSWORD_CHANGE_REQUIRED") {
        passwordChangeRequiredHandler();
        return Promise.reject(error);
      }

      if (
        errorCode === "USER_NOT_PROVISIONED" ||
        errorCode === "USER_DEACTIVATED"
      ) {
        unauthorizedHandler();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);
